import * as React from "react";
import ToggleButton from "../commons/components/buttons/ToggleButton";
import TextAreaInput from "../commons/components/form/input/TextAreaInput";
import { required } from "../commons/model/form/Validators";
import TextInput from "../commons/components/form/input/TextInput";
import sfetch from "../commons/utils/super-fetch";
import SettingsForm from "./SettingsForm";
import InputState from "../commons/model/form/InputState";
import UrlInput from "../commons/components/form/input/UrlInput";

export interface SettingPracticePageProps {
  title?: string;
  subtitle?: string;
  canSetDisplayPracticeDetails: boolean;
  displayPracticeDetails: boolean;
  logoRedirectionUrl?: string;
  maxTitleLength: number;
  maxSubtitleLength: number;
  defaultTitle: string;
  defaultSubtitle: string;
  updateUrl: string;
  confirmationRedirectionUrl?: string;
}

interface SettingVirtualConsultationCustomizationState {
  title: string;
  subtitle: string;
  displayPracticeDetails: boolean;
  logoRedirectionUrl: string;
  confirmationRedirectionUrl: string;
}

export default class SettingPracticePage extends React.PureComponent<
  SettingPracticePageProps,
  SettingVirtualConsultationCustomizationState
> {
  private inputStates: { [field: string]: InputState } = {};
  private readonly persistedState: SettingVirtualConsultationCustomizationState;

  constructor(props: SettingPracticePageProps) {
    super(props);
    this.persistedState = {
      title: this.props.title ? this.props.title : this.props.defaultTitle,
      subtitle: this.props.subtitle != null ? this.props.subtitle : this.props.defaultSubtitle,
      displayPracticeDetails: this.props.displayPracticeDetails,
      logoRedirectionUrl: this.props.logoRedirectionUrl || "",
      confirmationRedirectionUrl: this.props.confirmationRedirectionUrl || "",
    };
    this.state = { ...this.persistedState };
  }

  render() {
    return (
      <SettingsForm
        title={i18n["Page.Settings.VirtualConsultation.Customization.Generic.Title"]}
        hasChanges={this.hasChanges()}
        isValid={this.isValid()}
        onCancel={() => this.cancel()}
        onSave={() => this.save()}
        id={"virtual-consultation-customisation-generic-page-customisation"}
      >
        <p className="setting-description">
          {i18n["Page.Settings.VirtualConsultation.Customization.Subtitle"]}
        </p>

        <div className="field-groups">
          <h3 className="title">
            {i18n["Page.Settings.VirtualConsultation.Customization.LogoRedirection"]}
          </h3>
          <label htmlFor="title">
            {i18n["Page.Settings.VirtualConsultation.Customization.LogoRedirection.UrlField"]}
            &nbsp;{i18n["Global.OptionalField"]}
          </label>
          <UrlInput
            name="logoRedirectionUrl"
            initialValue={this.state.logoRedirectionUrl}
            onChange={(inputState) => {
              this.updateFieldState("logoRedirectionUrl", inputState);
            }}
          />
        </div>
        <hr />
        <div className="field-groups">
          <h3 className="title">
            {i18n["Page.Settings.VirtualConsultation.Customization.Catchline"]}
          </h3>
          <label htmlFor="title">
            {i18n["Page.Settings.VirtualConsultation.Customization.Catchline.TitleField"]}
          </label>
          <TextInput
            name="title"
            initialValue={this.state.title}
            maxLength={this.props.maxTitleLength}
            validators={[required()]}
            onChange={(inputState) => {
              this.updateFieldState("title", inputState);
            }}
          />
          <label htmlFor="subtitle">
            {i18n["Page.Settings.VirtualConsultation.Customization.Catchline.SubtitleField"]}
            &nbsp;{i18n["Global.OptionalField"]}
          </label>
          <TextAreaInput
            name="subtitle"
            initialValue={this.state.subtitle}
            maxLength={this.props.maxSubtitleLength}
            detail={i18n["Global.OptionalField"]}
            onChange={(inputState) => {
              this.updateFieldState("subtitle", inputState);
            }}
          />
        </div>
        <hr />
        {this.props.canSetDisplayPracticeDetails && (
          <>
            <div className="field-groups">
              <h3 className="title">
                {i18n["Page.Settings.VirtualConsultation.Customization.PracticeDetails"]}
              </h3>
              <div className={"vertical-container toggle"}>
                <ToggleButton
                  id={"display-practice-details-toggle"}
                  value={this.state.displayPracticeDetails}
                  onChange={(value) => {
                    this.updateState("displayPracticeDetails", value);
                  }}
                />
                <label>
                  {
                    i18n[
                      "Page.Settings.VirtualConsultation.Customization.PracticeDetails.Description"
                    ]
                  }
                </label>
              </div>
            </div>
            <hr />
          </>
        )}
        <div className="field-groups">
          <h3 className="title">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.ConfirmationRedirection.Title"
              ]
            }
          </h3>
          <label htmlFor="title">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.ConfirmationRedirection.UrlField"
              ]
            }
            &nbsp;{i18n["Global.OptionalField"]}
          </label>
          <UrlInput
            name="confirmationRedirectionUrl"
            initialValue={this.state.confirmationRedirectionUrl}
            onChange={(inputState) => {
              this.updateFieldState("confirmationRedirectionUrl", inputState);
            }}
          />
        </div>
      </SettingsForm>
    );
  }

  private hasChanges() {
    return Object.keys(this.state).some((field) => this.state[field] != this.persistedState[field]);
  }

  private isValid() {
    return !Object.values(this.inputStates).some((inputState) => inputState.status.invalid);
  }

  private updateFieldState(
    field: keyof SettingVirtualConsultationCustomizationState,
    inputState: InputState
  ) {
    this.inputStates[field] = inputState;
    this.updateState(field, inputState.value);
  }

  private updateState<F extends keyof SettingVirtualConsultationCustomizationState>(
    field: F,
    value: SettingVirtualConsultationCustomizationState[F]
  ) {
    if (value !== this.state[field]) {
      this.setState({ ...this.state, [field]: value });
    }
  }

  save() {
    const payload = { ...this.state };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") delete payload[key];
    });
    return sfetch(this.props.updateUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    }).then(() => document.location.reload());
  }

  cancel() {
    this.setState({ ...this.persistedState }, this.syncInputStates);
  }

  private syncInputStates() {
    for (const inputState of Object.values(this.inputStates)) {
      const field = inputState.name;
      console.assert(field in this.state, {
        msg: "field not found in state",
        field: field,
      });
      inputState.setValue(this.state[field]);
    }
  }
}
